import * as React from 'react'
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { type Toast as ToastType, ToastContext } from '@/hooks/useToast'

const DISMISS_DURATION = 4000

const variantConfig = {
  success: {
    stripe: 'bg-gradient-to-b from-teal-400 to-teal-600',
    iconBg: 'bg-teal-50',
    iconColor: 'text-teal-600',
    iconGlow: 'shadow-[0_0_12px_rgba(13,148,136,0.25)]',
    progressBar: 'bg-teal-500/60',
    titleColor: 'text-foreground',
    dismissColor: 'text-slate-400 hover:text-slate-600',
    Icon: CheckCircle2,
  },
  destructive: {
    stripe: 'bg-gradient-to-b from-rose-400 to-rose-600',
    iconBg: 'bg-rose-50',
    iconColor: 'text-rose-600',
    iconGlow: 'shadow-[0_0_12px_rgba(225,29,72,0.25)]',
    progressBar: 'bg-rose-500/60',
    titleColor: 'text-foreground',
    dismissColor: 'text-slate-400 hover:text-rose-500',
    Icon: AlertCircle,
  },
  default: {
    stripe: 'bg-gradient-to-b from-slate-300 to-slate-400',
    iconBg: 'bg-slate-50',
    iconColor: 'text-slate-500',
    iconGlow: '',
    progressBar: 'bg-slate-400/50',
    titleColor: 'text-foreground',
    dismissColor: 'text-slate-400 hover:text-slate-600',
    Icon: Info,
  },
}

interface ToastProps {
  toast: ToastType
  onDismiss: (id: string) => void
}

function Toast({ toast, onDismiss }: ToastProps) {
  const [exiting, setExiting] = React.useState(false)

  React.useEffect(() => {
    if (toast.persistent) return
    const timer = setTimeout(() => {
      setExiting(true)
      setTimeout(() => onDismiss(toast.id), 280)
    }, DISMISS_DURATION)
    return () => clearTimeout(timer)
  }, [toast.id, toast.persistent, onDismiss])

  const handleDismiss = React.useCallback(() => {
    setExiting(true)
    setTimeout(() => onDismiss(toast.id), 280)
  }, [toast.id, onDismiss])

  const variant = toast.variant || 'default'
  const config = variantConfig[variant]
  const { Icon } = config

  return (
    <div
      className={cn(
        'group pointer-events-auto relative flex w-full overflow-hidden rounded-xl bg-white shadow-lg shadow-black/8 ring-1 ring-black/[0.04]',
        'animate-toast-in',
        exiting && 'animate-toast-out',
      )}
    >
      {/* Left accent stripe */}
      <div className={cn('w-1 shrink-0 rounded-l-xl', config.stripe)} />

      {/* Content */}
      <div className="flex flex-1 items-start gap-3 px-4 py-3.5">
        {/* Icon with glow */}
        <div
          className={cn(
            'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
            config.iconBg,
            config.iconGlow,
          )}
        >
          <Icon className={cn('h-4 w-4', config.iconColor)} />
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1 pt-0.5">
          <p className={cn('text-[13px] font-semibold leading-tight', config.titleColor)}>
            {toast.title}
          </p>
          {toast.description && (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {toast.description}
            </p>
          )}
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className={cn(
            'mt-0.5 shrink-0 cursor-pointer rounded-md p-0.5 opacity-0 transition-all duration-150',
            'group-hover:opacity-100 focus:opacity-100 focus:outline-none',
            config.dismissColor,
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Progress bar — drains from right to left */}
      {!toast.persistent && (
        <div className="absolute inset-x-0 bottom-0 h-[2px] overflow-hidden">
          <div
            className={cn(
              'h-full origin-right',
              config.progressBar,
              'animate-toast-progress',
            )}
          />
        </div>
      )}
    </div>
  )
}

function ToastViewport({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const context = React.useContext(ToastContext)
  if (!context) return null
  if (context.toasts.length === 0) return null

  return (
    <div
      className={cn(
        'fixed top-16 left-4 right-4 z-[100] flex w-auto flex-col gap-2 sm:left-auto sm:right-4 sm:w-full sm:max-w-[380px]',
        className,
      )}
      {...props}
    >
      {context.toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={context.removeToast} />
      ))}
    </div>
  )
}

export { Toast, ToastViewport }
